import * as React from "react"
import { Link, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import Bio from "../components/bio"
import Layout from "../components/layout"
import Seo from "../components/seo"
import Tags from "../components/tags"

const BlogPostTemplate = ({ data, location }) => {
  const post = data.markdownRemark
  const siteTitle = data.site.siteMetadata?.title || `Title`
  const tags = post.frontmatter.tags || []
  const { previous, next } = data
  const image =
    post.frontmatter.featuredImage
      ? getImage(post.frontmatter.featuredImage.src.childImageSharp)
      : null
  const imageCaption = post.frontmatter.featuredImage?.caption || null
  const toc = post.tableOfContents
  const [showToc, setShowToc] = React.useState(true)

  // determine when viewport is too narrow to display toc alongside a full-width
  // article. calculate threshold based on the wrapper max-width + sidebar
  // width/gap.  we do it in JS because CSS media queries with dynamic rem
  // values weren't precise enough for the user's needs.
  React.useEffect(() => {
    const computeThreshold = () => {
      const wrapper = document.querySelector('.global-wrapper')
      if (!wrapper) return 0
      const cs = window.getComputedStyle(wrapper)
      let maxW = cs.getPropertyValue('max-width')
      // convert rem to px if necessary
      if (maxW.endsWith('rem')) {
        const remVal = parseFloat(maxW)
        const rootSize = parseFloat(
          getComputedStyle(document.documentElement).fontSize
        )
        maxW = remVal * rootSize
      } else {
        maxW = parseFloat(maxW)
      }
      // toc width + gap (200px + ~32px) plus a little padding
      const extra = 200 + 32 + 10
      // add a buffer so it hides slightly *before* the article would start
      // to shrink; prevents any overlap during shrink. increasing threshold
      // means the TOC disappears sooner.
      const buffer = 150
      return maxW + extra + buffer
    }

    let threshold = computeThreshold()
    const update = () => {
      setShowToc(window.innerWidth >= threshold)
    }
    update()
    window.addEventListener('resize', update)

    // recompute threshold if fonts or css change (unlikely) but a resize will
    // recalc anyway
    const observer = new MutationObserver(() => {
      threshold = computeThreshold()
      update()
    })
    observer.observe(document.documentElement, { attributes: true })

    return () => {
      window.removeEventListener('resize', update)
      observer.disconnect()
    }
  }, [])

  return (
    <Layout location={location} title={siteTitle}>
      <Seo
        title={post.frontmatter.title}
        description={post.frontmatter.description || post.excerpt}
        tags={tags}
      />
      {toc && showToc && (
        <nav
          className="toc"
          dangerouslySetInnerHTML={{ __html: toc }}
          aria-label="Table of contents"
        />
      )}
      <div className="post-container">
        <article
          className="blog-post"
          itemScope
          itemType="http://schema.org/Article"
        >
        <header>
          <h1 itemProp="headline">{post.frontmatter.title}</h1>
          <p>{post.frontmatter.date}  &middot;  {post.timeToRead} minutes to read</p>
        </header>
        <Tags listOfTags={tags} />
        {image && (
          <figure>
            <GatsbyImage
              image={image}
              alt={post.frontmatter.featuredImage.alt}
            />
            {imageCaption && <figcaption>{imageCaption}</figcaption>}
          </figure>
        )}
        <section
          dangerouslySetInnerHTML={{ __html: post.html }}
          itemProp="articleBody"
        />
        <hr />
        <footer>
          <Bio />
        </footer>
      </article>
      </div>
      <nav className="blog-post-nav">
        <ul
          style={{
            display: `flex`,
            flexWrap: `wrap`,
            justifyContent: `space-between`,
            listStyle: `none`,
            padding: 0,
          }}
        >
          <li>
            {previous && (
              <Link to={previous.fields.slug} rel="prev">
                ← {previous.frontmatter.title}
              </Link>
            )}
          </li>
          <li>
            {next && (
              <Link to={next.fields.slug} rel="next">
                {next.frontmatter.title} →
              </Link>
            )}
          </li>
        </ul>
      </nav>
    </Layout>
  )
}

export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostBySlug(
    $id: String!
    $previousPostId: String
    $nextPostId: String
  ) {
    site {
      siteMetadata {
        title
      }
    }
    markdownRemark(id: { eq: $id }) {
      id
      excerpt(pruneLength: 160)
      html
      tableOfContents(maxDepth: 3)
      frontmatter {
        title
        date(formatString: "MMMM DD, YYYY")
        description
        tags
        featuredImage {
          src {
            childImageSharp {
              gatsbyImageData(width: 632)
            }
          }
          alt
          caption
        }
      }
      timeToRead
    }
    previous: markdownRemark(id: { eq: $previousPostId }) {
      fields {
        slug
      }
      frontmatter {
        title
      }
    }
    next: markdownRemark(id: { eq: $nextPostId }) {
      fields {
        slug
      }
      frontmatter {
        title
      }
    }
  }
`
